// Need to use the React-specific entry point to import createApi
import config from "../config";
import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareHeaders } from "./utils";
import { Answer, QuestionGroupWithRelations } from "../../../api/src/types";
import {
  createQuestionnaireSectionDetailsTagId,
  createQuestionnaireSectionsTagId,
  questionnaireApi,
} from "./questionnaire";
import { createSerializedFetchBaseQuery } from "./serializedFetchBaseQuery";
import type {
  AnswersDisputeGetResponse,
  AnswersWithAttorneyRestrictionsGetResponse,
} from "../../../api/src/users/answers/route";
import { ResolutionsGetResponse } from "../../../api/src/users/answers/resolutions/route";
import { documentsApi } from "./documents";
import { clearQuestionAnswerTreeInCache } from "./answerTreeCache";
import {
  buildSerializedAnswerMutationHeaders,
  buildUpsertAnswerRequestBody,
  createCleanSlateTracker,
  patchQueryDataIfPresent,
  runOptimisticAnswerMutation,
} from "./answerMutationHelpers";

type QuestionnaireModalAnswer =
  QuestionGroupWithRelations["questions"][number]["answers"][number];

// todo: use hono response type from backend instead
interface AnswerUpsertMutationResponse<TAnswer> {
  kind: "no-op" | "create" | "update" | "delete";
  answer?: TAnswer;
  deletedChildQuestionIds: string[];
}

const answersCleanSlateTracker = createCleanSlateTracker({
  createKey: (params: {
    userId: string;
    documentId: string;
    questionId: string;
  }): string => {
    return `answers-clean-slate:${params.userId}:${params.documentId}:${params.questionId}`;
  },
});

export const answersApi = createApi({
  reducerPath: "answersApi",
  baseQuery: createSerializedFetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["answers", "documents", "disputes", "resolutions"],
  endpoints: (builder) => ({
    getAnswer: builder.query<
      Answer[],
      { questionId?: string; userId: string; documentId: string }
    >({
      query: ({ questionId, userId, documentId }) => ({
        url: `/users/${userId}/answers`,
        params: { documentId, ...(questionId ? { questionId } : {}) },
      }),
      providesTags: ["answers"],
    }),
    upsertAnswer: builder.mutation<
      AnswerUpsertMutationResponse<Answer>,
      {
        questionId: string;
        userId: string;
        documentId: string;
        rootQuestionId: string;
        sectionId: string;
        serializeDebounceMs?: number;
        valueTyped?: string | null;
        questionOptionIds?: string[];
      }
    >({
      query: ({
        questionId,
        userId,
        documentId,
        serializeDebounceMs,
        valueTyped,
        questionOptionIds,
      }) => ({
        url: `/users/${userId}/answers`,
        method: "POST",
        params: { questionId, documentId },
        body: buildUpsertAnswerRequestBody({
          valueTyped,
          questionOptionIds,
          forceCleanSlate: answersCleanSlateTracker.has({
            userId,
            documentId,
            questionId,
          }),
        }),
        headers: buildSerializedAnswerMutationHeaders({
          serializeBy: documentId,
          pruneBy: `${questionId}-POST`,
          serializeDebounceMs,
        }),
      }),
      async onQueryStarted(
        {
          questionId,
          userId,
          documentId,
          valueTyped,
          questionOptionIds,
          sectionId,
        },
        api,
      ) {
        const sectionGroupsArgs = {
          docId: documentId,
          section: sectionId,
          userId,
        } as const;

        await runOptimisticAnswerMutation({
          dispatch: api.dispatch,
          getState: () => api.getState(),
          queryFulfilled: api.queryFulfilled,
          cache: {
            args: sectionGroupsArgs,
            selectQueryState: (state, args) =>
              questionnaireApi.endpoints.getQuestionnaireSectionGroupsByDocumentId.select(
                args,
              )(state),
            updateQueryData: (args, updater) =>
              questionnaireApi.util.updateQueryData(
                "getQuestionnaireSectionGroupsByDocumentId",
                args,
                updater,
              ),
          },
          optimisticTreeUpdate: {
            questionId,
            valueTyped,
            questionOptionIds,
            createOptimisticAnswer: ({
              questionId,
              normalizedValueTyped,
              normalizedOptionIds,
              now,
            }) => {
              const tempId = `temp-${now.getTime()}`;

              return {
                id: tempId,
                questionId,
                userId,
                documentId,
                valueTyped: normalizedValueTyped ?? null,
                deletedAt: null,
                createdAt: now,
                updatedAt: now,
                createdById: userId,
                updatedById: userId,
                answerQuestionOptions: normalizedOptionIds.map((optionId) => ({
                  questionOptionId: optionId,
                  answerId: tempId,
                  isDefault: false,
                  createdAt: now,
                  updatedAt: now,
                })),
              } as QuestionnaireModalAnswer;
            },
            createAnswerQuestionOption: ({ answerId, optionId, now }) => ({
              questionOptionId: optionId,
              answerId,
              isDefault: false,
              createdAt: now,
              updatedAt: now,
            }),
          },
          cleanSlateKey: { userId, documentId, questionId },
          cleanSlateTracker: answersCleanSlateTracker,
          onError: ({ error, dispatch, state, cacheArgs }) => {
            patchQueryDataIfPresent({
              dispatch,
              state,
              args: cacheArgs,
              selectQueryState: (state, args) =>
                questionnaireApi.endpoints.getQuestionnaireSectionGroupsByDocumentId.select(
                  args,
                )(state),
              updateQueryData: (args, updater) =>
                questionnaireApi.util.updateQueryData(
                  "getQuestionnaireSectionGroupsByDocumentId",
                  args,
                  updater,
                ),
              updater: (draft) => {
                clearInCache({
                  draft,
                  parentQuestionId: questionId,
                });
              },
            });
            console.error("Failed to upsert answer:", error);
          },
          onFinally: ({ dispatch }) => {
            const questionnaireTags = [
              {
                type: "QuestionnaireSections" as const,
                id: createQuestionnaireSectionDetailsTagId({
                  userId,
                  section: sectionId,
                }),
              },
              {
                type: "QuestionnaireSections" as const,
                id: createQuestionnaireSectionsTagId({
                  userId,
                  includeStatus: true,
                }),
              },
              { type: "QuestionOptionVisibility" as const },
            ];
            dispatch(questionnaireApi.util.invalidateTags(questionnaireTags));
            dispatch(
              documentsApi.util.invalidateTags([
                "documents",
                "regeneration-status",
              ]),
            );
          },
        });
      },
      invalidatesTags: ["answers"],
    }),
    getAnswersWithAttorneyRestrictions: builder.query<
      AnswersWithAttorneyRestrictionsGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `/users/${userId}/answers/attorney-restrictions`,
        params: { documentId },
      }),
      providesTags: ["answers"],
    }),
    getDisputes: builder.query<
      AnswersDisputeGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `/users/${userId}/answers/dispute`,
        params: { documentId },
      }),
      providesTags: ["disputes"],
    }),
    getResolutions: builder.query<
      ResolutionsGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `/users/${userId}/documents/${documentId}/resolutions`,
      }),
      providesTags: ["resolutions"],
    }),
  }),
});

export const {
  useGetAnswerQuery,
  useUpsertAnswerMutation,
  useGetAnswersWithAttorneyRestrictionsQuery,
  useGetDisputesQuery,
  useGetResolutionsQuery,
} = answersApi;

function clearInCache(params: {
  draft: QuestionGroupWithRelations[];
  parentQuestionId: string;
}): void {
  clearQuestionAnswerTreeInCache(params);
}
